import { memo, useEffect } from "react";
import { StyleProp, ViewStyle } from "react-native";
import Animated, { FadeIn, FadeOut } from "react-native-reanimated";
import ImageGallery from "src/components/ImageGallery";
import useBumpToastHeight from "src/hooks/toast-height/useBumpToastHeight";
import { GatheringOrLocalPhoto } from "src/hooks/useGatheringPhotosWithLocal";
import { useSafeArea } from "src/swsh-native";
import useImageGalleryDimensions from "../hooks/useImageGalleryDimensions";
import useImageGalleryProps from "../hooks/useImageGalleryProps";
import usePhotoDetailDismiss from "../hooks/usePhotoDetailDismiss";
import usePhotoDetailTagTracking from "../hooks/usePhotoDetailTagTracking";
import useUpdateUrlState from "../hooks/useUpdateUrlState";
import usePhotoDetailStore from "../stores/usePhotoDetailStore";
import PhotoDetailDataManagement from "./PhotoDetailDataManagement";
import PhotoDetailUI from "./PhotoDetailUI";

const PhotoDetailContent = ({
	photoId,
	photos,
	fetchMore,
	fetchMoreReverse,
}: {
	photoId: string;
	photos: GatheringOrLocalPhoto[];
	fetchMore?: () => void;
	fetchMoreReverse?: () => void;
}) => {
	useBumpToastHeight(80);
	usePhotoDetailTagTracking(photoId);

	const safeArea = useSafeArea();
	const imageGalleryProps = useImageGalleryProps({
		photoId,
	});

	const mainItemContainerStyle: StyleProp<ViewStyle> = {};

	const dimensions = useImageGalleryDimensions();

	useEffect(() => {
		usePhotoDetailStore.setState({ isWaitingForInitialPhotoLoad: true });
		// Give up after 3 seconds
		const timeout = setTimeout(() => {
			usePhotoDetailStore.setState({
				isWaitingForInitialPhotoLoad: false,
			});
		}, 3000);
		return () => {
			usePhotoDetailStore.getState().clear();
			clearTimeout(timeout);
		};
	}, []);

	const mode = usePhotoDetailStore((state) => state.mode);
	useUpdateUrlState();
	const hideNavigation = usePhotoDetailStore(
		(state) => state.hideNavigationSubscribers.length > 0,
	);
	const { animatedStyle, mainGesturePanListener } = usePhotoDetailDismiss();

	return (
		<Animated.View style={animatedStyle}>
			<PhotoDetailDataManagement />
			<ImageGallery
				{...dimensions}
				data={photos}
				initialKey={photoId}
				mainItemContainerStyle={mainItemContainerStyle}
				carouselItemContainerStyle={{
					bottom: safeArea.bottom + dimensions.carouselItemDimensions.paddingVertical,
				}}
				onStartReached={fetchMoreReverse}
				onEndReached={fetchMore}
				onActiveItem={({ item }) => {
					usePhotoDetailStore.getState().setCurPhoto(item);
				}}
				carouselContainerAnimated={{
					entering: FadeIn,
					exiting: FadeOut,
				}}
				showCarousel={mode === "Carousel"}
				{...imageGalleryProps}
				showNavigation={!hideNavigation && mode === "Regular"}
				mainGesturePanListener={mainGesturePanListener}
			/>
			<PhotoDetailUI />
		</Animated.View>
	);
};
export default memo(PhotoDetailContent);
